import React from "react"
import {Coins, RefreshCw, Save, Settings} from 'lucide-react'
import type { PointsSetting, SiteSetting, SiteSettingsTabKey } from "./siteSettingsTypes"

export function SiteSettingsHeader() {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-slate-600 flex items-center justify-center">
          <Settings size={24} className="text-white" />
        </div>
        <h2 className="text-3xl font-light tracking-wider text-gray-100">網站設定</h2>
      </div>
      <p className="text-gray-400 ml-[52px]">集中管理網站功能開關，變更將即時影響前台顯示</p>
    </div>
  )
}

export function SiteSettingsTabsBar(props: {
  tabs: { key: SiteSettingsTabKey; label: string }[]
  activeTab: SiteSettingsTabKey
  onTabChange: (key: SiteSettingsTabKey) => void
  onSave: () => void
  saving: boolean
  saveDisabled: boolean
}) {
  const { tabs, activeTab, onTabChange, onSave, saving, saveDisabled } = props

  return (
    <div className="mb-6 border-b border-gray-800">
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              className={`px-6 py-3 text-sm font-light tracking-wide transition-colors relative ${
                activeTab === tab.key
                  ? "text-blue-400 border-b-2 border-blue-400"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          onClick={onSave}
          disabled={saving || saveDisabled}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors mb-1"
        >
          {saving ? (
            <>
              <RefreshCw size={18} className="animate-spin" />
              <span>保存中...</span>
            </>
          ) : (
            <>
              <Save size={18} />
              <span>保存設定</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export function SiteSettingsMessage(props: { message: { type: "success" | "error"; text: string } | null }) {
  const { message } = props
  if (!message) return null

  return (
    <div
      className={`mb-6 p-4 border ${
        message.type === "success"
          ? "bg-green-950 border-green-800 text-green-200"
          : "bg-red-950 border-red-800 text-red-200"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm">{message.text}</span>
      </div>
    </div>
  )
}

export function PointsSystemTab(props: {
  pointsSetting: PointsSetting | null
  pointsPerDollar: number
  minPointsToUse: number
  pointsIsActive: boolean
  pointsDescription: string
  setPointsPerDollar: (n: number) => void
  setMinPointsToUse: (n: number) => void
  setPointsIsActive: (v: boolean) => void
  setPointsDescription: (v: string) => void
}) {
  const {
    pointsSetting,
    pointsPerDollar,
    minPointsToUse,
    pointsIsActive,
    pointsDescription,
    setPointsPerDollar,
    setMinPointsToUse,
    setPointsIsActive,
    setPointsDescription
  } = props

  const examplePoints = 1000
  const exampleDollar = pointsPerDollar > 0 ? examplePoints / pointsPerDollar : 0

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="bg-gray-950 border border-gray-800 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Coins size={24} className="text-blue-400" />
              <h3 className="text-xl font-light tracking-wider">點數折扣設定</h3>
            </div>

            <div className="space-y-6">
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pointsIsActive}
                    onChange={(e) => setPointsIsActive(e.target.checked)}
                    className="w-5 h-5 bg-gray-900 border border-gray-700 checked:bg-blue-600 checked:border-blue-600 cursor-pointer"
                  />
                  <div>
                    <div className="text-gray-100 font-medium">啟用點數折扣功能</div>
                    <div className="text-xs text-gray-500">關閉後會員無法在結帳時使用點數</div>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  點數兌換比例 <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={pointsPerDollar}
                    onChange={(e) => setPointsPerDollar(Number(e.target.value))}
                    min={1}
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400 whitespace-nowrap">分 = NT$ 1</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">例如：設定為 100，表示 100 點 = NT$ 1 元</div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  最低使用點數門檻 <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={minPointsToUse}
                    onChange={(e) => setMinPointsToUse(Number(e.target.value))}
                    min={0}
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-gray-400">分</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">會員至少需要此數量的點數才能使用折扣</div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">設定說明（選填）</label>
                <textarea
                  value={pointsDescription}
                  onChange={(e) => setPointsDescription(e.target.value)}
                  rows={3}
                  placeholder="輸入此設定的說明或備註..."
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500 resize-none"
                />
                {pointsSetting ? (
                  <div className="text-xs text-gray-600 mt-2">目前使用中記錄：{pointsSetting._id}</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-950 border border-gray-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4">當前設定摘要</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-800">
                <span className="text-gray-400">功能狀態</span>
                <span className={`font-medium ${pointsIsActive ? "text-green-400" : "text-red-400"}`}>
                  {pointsIsActive ? "✓ 已啟用" : "✕ 已停用"}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-800">
                <span className="text-gray-400">兌換比例</span>
                <span className="text-gray-100">{pointsPerDollar} 分 = NT$ 1</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-400">最低使用</span>
                <span className="text-gray-100">{minPointsToUse} 分</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-950 border border-blue-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4 text-blue-100">使用示例</h3>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-blue-200 mb-2">📊 點數價值計算：</div>
                <div className="bg-blue-900 bg-opacity-30 p-3 rounded">
                  <div className="text-blue-100">
                    會員有 <span className="font-bold">{examplePoints.toLocaleString()}</span> 點
                  </div>
                  <div className="text-blue-300 text-xs mt-1">
                    = NT$ <span className="font-bold">{exampleDollar.toLocaleString()}</span> 元折扣
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-950 border border-gray-800 p-6">
            <h3 className="text-xl font-light tracking-wider mb-4">⚠️ 注意事項</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>修改設定後會立即生效，影響所有新訂單</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-600">•</span>
                <span>停用功能後，會員仍可累積點數但無法使用</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ConvenienceStorePickupTab(props: {
  setting: SiteSetting | undefined
  ezshipSuId: string
  onToggle: (id: string) => void
  onChangeEzshipSuId: (v: string) => void
}) {
  const { setting, ezshipSuId, onToggle, onChangeEzshipSuId } = props
  if (!setting) {
    return (
      <div className="mb-8 p-6 bg-gray-950 border border-gray-800 text-gray-400">此設定項目不存在或已被移除</div>
    )
  }

  return (
    <div className="mb-8">
      <div className="bg-gray-950 border border-gray-800 p-6 hover:border-gray-700 transition-colors">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="flex-1">
            <h3 className="text-xl font-light tracking-wide text-gray-100 mb-2">{setting.displayName}</h3>
            <p className="text-sm text-gray-400 mb-3">{setting.description}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>識別碼: {setting.settingKey}</span>
              <span>最後更新: {new Date(setting.updatedAt).toLocaleString("zh-TW")}</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <button
              onClick={() => onToggle(setting._id)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                setting.enabled ? "bg-green-600" : "bg-gray-700"
              }`}
              aria-label={`切換 ${setting.displayName}`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  setting.enabled ? "translate-x-9" : "translate-x-1"
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${setting.enabled ? "text-green-400" : "text-gray-500"}`}>
              {setting.enabled ? "已啟用" : "已停用"}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                ezShip 商家账号 (SU ID) <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={ezshipSuId}
                onChange={(e) => onChangeEzshipSuId(e.target.value)}
                placeholder="例如：service@i-tea.com.tw"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:border-blue-500"
              />
              <div className="text-xs text-gray-500 mt-2">此账号用于调用 ezShip 超商电子地图服务</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
